import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  Activity, Calendar, MessageSquare, FileText,
  Clock, BookOpen,
  ClipboardList, Pill, ChevronRight, CircleDot, FlaskConical, FolderOpen,
  CheckCircle, PlayCircle, Route, ListTodo, AlertTriangle, Stethoscope
} from "lucide-react";
import { getItems } from "@/utils/apiHelpers";
import { PatientSpeakerButton, usePatientAudioCache } from "@/components/shared/PatientSpeakerButton";
import { isScheduledCheckinDue } from "@/utils/checkinFrequency";
import BiologyUploadScanner from "@/components/patient/BiologyUploadScanner";

export default function PatientHome() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const audioCache = usePatientAudioCache();
  const [patient, setPatient] = useState(null);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [lastScheduledCheckin, setLastScheduledCheckin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingQuestionnaires, setPendingQuestionnaires] = useState([]);
  const [oralDoses, setOralDoses] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [educationRecommendation, setEducationRecommendation] = useState(null);
  const [showPreChemoScanner, setShowPreChemoScanner] = useState(false);

  useEffect(() => {
    fetchData();
    api.post("/patients/heartbeat").catch(() => {});
  }, []);

  useEffect(() => {
    if (loading || !patient) return;
    const params = new URLSearchParams(location.search);
    if (params.get("skipAutoCheckin") === "1") {
      sessionStorage.setItem(`hana_skip_auto_checkin_${user.id}_${new Date().toDateString()}`, "1");
      return;
    }
    const alreadySkipped = sessionStorage.getItem(`hana_skip_auto_checkin_${user.id}_${new Date().toDateString()}`) === "1";
    const alreadyRedirected = sessionStorage.getItem(`hana_auto_checkin_${user.id}_${new Date().toDateString()}`) === "1";
    if (!alreadySkipped && !alreadyRedirected && isScheduledCheckinDue(patient, lastScheduledCheckin)) {
      sessionStorage.setItem(`hana_auto_checkin_${user.id}_${new Date().toDateString()}`, "1");
      navigate("/patient/checkin?mode=scheduled&from=auto", { replace: true });
    }
  }, [loading, patient, lastScheduledCheckin, location.search, navigate, user.id]);

  const fetchData = async () => {
    // Fetch each resource independently so one failure doesn't block the rest
    try {
      const patRes = await api.get(`/patients/${user.id}`);
      setPatient(patRes.data);
    } catch (err) {
      toast.error(t("patient.loadError"));
    }

    try {
      const checkinRes = await api.get(`/patients/${user.id}/checkins?limit=10`);
      const scheduled = (checkinRes.data || []).find(c => c.checkinType !== "spontaneous_report");
      setLastScheduledCheckin(scheduled || null);
      if (scheduled) {
        const lastCheckin = new Date(scheduled.createdAt);
        const today = new Date();
        setHasCheckedInToday(
          lastCheckin.toDateString() === today.toDateString()
        );
      } else {
        setHasCheckedInToday(false);
      }
    } catch {
      // Checkin status non-critical
    }

    try {
      const threadRes = await api.get("/threads");
      const threads = getItems(threadRes.data);
      const totalUnread = threads.reduce((sum, t) => sum + (t.unreadCount || 0), 0);
      setUnreadMessages(totalUnread);
    } catch {
      // Messages non-critical
    }

    try {
      const pendingRes = await api.get("/studies/my-pending");
      setPendingQuestionnaires(pendingRes.data || []);
    } catch {
      // Questionnaires non-critical
    }

    try {
      const oralRes = await api.get("/patient/oral-adherence/today");
      setOralDoses(oralRes.data?.items || []);
    } catch {
      // Observance per os non-critical
    }

    try {
      const appointmentRes = await api.get(`/appointments?patientId=${user.id}`);
      setAppointments(Array.isArray(appointmentRes.data) ? appointmentRes.data : getItems(appointmentRes.data));
    } catch {
      // Appointments non-critical
    }

    try {
      const educationRes = await api.get("/education/patient/recommended?limit=1");
      setEducationRecommendation((educationRes.data || [])[0] || null);
    } catch {
      // Education non-critical
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center h-48">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </PatientLayout>
    );
  }

  const statusConfig = {
    STABLE: {
      label: t("status.STABLE"),
      pill: "bg-emerald-50 text-emerald-700 border-emerald-100",
      dot: "bg-emerald-500",
    },
    TO_MONITOR: {
      label: t("status.TO_MONITOR"),
      pill: "bg-orange-50 text-orange-700 border-orange-100",
      dot: "bg-orange-500",
    },
    OPEN_ALERT: {
      label: t("status.OPEN_ALERT") || "Alerte ouverte",
      pill: "bg-amber-50 text-amber-800 border-amber-100",
      dot: "bg-amber-500",
    },
    CRITICAL: {
      label: t("patient.statusLabels.CRITICAL"),
      pill: "bg-rose-50 text-rose-700 border-rose-100",
      dot: "bg-rose-500",
    },
    HOSPITALIZED: {
      label: t("status.HOSPITALIZED"),
      pill: "bg-sky-50 text-sky-700 border-sky-100",
      dot: "bg-sky-500",
    },
  };
  const patientStatus = statusConfig[patient?.status || "STABLE"] || statusConfig.STABLE;
  const urgentOralDoses = oralDoses.filter(dose => ["pending", "missed"].includes(dose.status));
  const checkinDue = isScheduledCheckinDue(patient, lastScheduledCheckin);
  const pendingQuestionnaireCount = pendingQuestionnaires.length;
  const appointmentToConfirm = appointments.find(appt => appt.status === "PROPOSED" && appt.requestedByRole !== "PATIENT");
  const nextChemoAppointment = getNextChemoAppointment(appointments);
  const L = getPatientHomeCopy(language);
  const todayActions = buildTodayActions({
    L,
    checkinDue,
    hasCheckedInToday,
    urgentOralDoses,
    pendingQuestionnaireCount,
    unreadMessages,
    appointmentToConfirm,
    nextChemoAppointment,
    educationRecommendation,
    setShowPreChemoScanner,
  }).slice(0, 5);
  const timelineItems = buildTimelineItems({
    L,
    language,
    patient,
    checkinDue,
    hasCheckedInToday,
    lastScheduledCheckin,
    urgentOralDoses,
    pendingQuestionnaireCount,
    nextChemoAppointment,
    nextAppointment: getNextAppointment(appointments),
    educationRecommendation,
  });
  return (
    <PatientLayout>
      <div className="space-y-4 pb-2 overflow-x-hidden" data-testid="patient-home-omnidoc-inspired">
        {showPreChemoScanner && (
          <BiologyUploadScanner
            onClose={() => setShowPreChemoScanner(false)}
            onSuccess={() => setShowPreChemoScanner(false)}
          />
        )}
        <div className="animate-fade-in-up" data-testid="patient-journey-header">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-[#2C5E53] leading-tight" data-testid="patient-home-greeting">
                {t("patient.greeting")}, {user?.firstName}
              </p>
              <h1 className="text-2xl font-extrabold leading-tight text-slate-800 tracking-normal" data-testid="patient-journey-title">
                {t("patient.journey.title")}
              </h1>
              <p className="mt-0.5 text-xs font-bold text-slate-400" data-testid="patient-journey-date">
                {new Date().toLocaleDateString(language === "ar" ? "ar-MA" : "fr-FR", {
                  weekday: "long", day: "numeric", month: "long"
                })}
              </p>
            </div>
            {patient && (
              <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 flex-shrink-0 ${patientStatus.pill}`} data-testid="patient-status-pill">
                <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${patientStatus.dot}`} data-testid="patient-status-dot" />
                <span className="text-xs font-extrabold leading-none" data-testid="patient-status-friendly-label">
                  {patientStatus.label}
                </span>
              </div>
            )}
          </div>
        </div>

        <TodayActionsPanel actions={todayActions} labels={L} />

        <PatientJourneyTimeline items={timelineItems} title={L.timelineTitle} />

        <div className="animate-fade-in-up stagger-2" data-testid="patient-intentions-section">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-extrabold text-slate-800" data-testid="patient-intentions-title">
              {L.quickActionsTitle}
            </p>
            <span className="rounded-full bg-[#E9F0EE] px-3 py-1 text-[11px] font-extrabold text-[#2C5E53]" data-testid="patient-intentions-count">
              {urgentOralDoses.length + pendingQuestionnaireCount + (checkinDue ? 1 : 0)} {t("patient.journey.pendingShort")}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2.5" data-testid="patient-intention-cards">
            <IntentionCard
              to="/patient/checkin?mode=scheduled"
              testId="patient-intention-checkin-card"
              icon={Activity}
              title={L.checkinTitle}
              subtitle={L.checkinSubtitle}
              tone={checkinDue ? "terracotta" : "sage"}
              audioKey="home_symptoms"
              audioText={t("patient.audioMessages.home_symptoms")}
              audioCache={audioCache}
            />
            <IntentionCard
              to="/patient/checkin?mode=spontaneous"
              testId="patient-intention-report-card"
              icon={CircleDot}
              title={L.reportTitle}
              subtitle={L.reportSubtitle}
              tone="rose"
              audioKey="home_report_problem"
              audioText={t("patient.audioMessages.home_report_problem")}
              audioCache={audioCache}
            />
            <IntentionCard
              to="/patient/questionnaires"
              testId="patient-intention-questionnaires-card"
              icon={ClipboardList}
              title={L.questionnairesTitle}
              subtitle={L.questionnairesSubtitle}
              tone="slate"
              badge={pendingQuestionnaireCount}
              audioKey="home_questions"
              audioText={t("patient.audioMessages.home_questions")}
              audioCache={audioCache}
            />
            <IntentionCard
              to="/patient/oral-adherence"
              testId="patient-intention-treatment-card"
              icon={Pill}
              title={L.treatmentTitle}
              subtitle={L.treatmentSubtitle}
              tone={urgentOralDoses.length > 0 ? "amber" : "sage"}
              badge={urgentOralDoses.length}
              audioKey="home_medicine"
              audioText={t("patient.audioMessages.home_medicine")}
              audioCache={audioCache}
            />
            <IntentionCard
              testId="patient-intention-prechemo-results-card"
              icon={FlaskConical}
              title={L.preChemoTitle}
              subtitle={L.preChemoSubtitle}
              tone="blue"
              onClick={() => setShowPreChemoScanner(true)}
              audioKey="home_bilan"
              audioText={t("patient.audioMessages.home_bilan")}
              audioCache={audioCache}
            />
            <IntentionCard
              to="/patient/medical-record?upload=1"
              testId="patient-intention-other-documents-card"
              icon={FileText}
              title={L.otherDocsTitle}
              subtitle={L.otherDocsSubtitle}
              tone="green"
              audioKey="home_documents"
              audioText={t("patient.audioMessages.home_documents")}
              audioCache={audioCache}
            />
            <IntentionCard
              to="/patient/medical-record"
              testId="patient-intention-folder-card"
              icon={FolderOpen}
              title={L.folderTitle}
              subtitle={L.folderSubtitle}
              tone="slate"
              audioKey="home_documents"
              audioText={t("patient.audioMessages.home_documents")}
              audioCache={audioCache}
            />
            <IntentionCard
              to="/patient/education"
              testId="patient-intention-education-card"
              icon={BookOpen}
              title={L.educationTitle}
              subtitle={L.educationSubtitle}
              tone="amber"
              audioKey="home_advice"
              audioText={t("patient.audioMessages.home_advice")}
              audioCache={audioCache}
            />
          </div>
        </div>

        <div className="animate-fade-in-up stagger-3" data-testid="patient-services-section">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-extrabold text-slate-800" data-testid="patient-services-title">
              {t("patient.journey.myServices")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="patient-services-grid">
            <ServiceTile
              to="/patient/appointments"
              testId="patient-service-appointment-tile"
              icon={Calendar}
              title={t("patient.journey.myAppointments")}
              audioKey="home_appointments"
              audioCache={audioCache}
              audioText={t("patient.audioMessages.home_appointments")}
              tone="blue"
            />
            <ServiceTile
              to="/patient/messages"
              testId="patient-service-team-tile"
              icon={MessageSquare}
              title={t("patient.journey.myTeam")}
              audioKey="home_team"
              audioCache={audioCache}
              audioText={t("patient.audioMessages.home_team")}
              badge={unreadMessages}
              tone="green"
            />
          </div>
        </div>

        {patient && (
          <CheckinFrequencyInfo patient={patient} hasCheckedInToday={hasCheckedInToday} checkinDue={checkinDue} />
        )}
      </div>
    </PatientLayout>
  );
}

function TodayActionsPanel({ actions, labels }) {
  const navigate = useNavigate();
  return (
    <section className="animate-fade-in-up stagger-1 rounded-[1.55rem] border border-[#2C5E53]/10 bg-white/95 p-3 shadow-[0_10px_30px_rgb(15,23,42,0.05)]" data-testid="patient-today-actions-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#E9F0EE] text-[#2C5E53]" data-testid="patient-today-actions-icon">
            <ListTodo className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-black leading-tight text-slate-800" data-testid="patient-today-actions-title">{labels.todayActionsTitle}</h2>
            <p className="text-[11px] font-semibold text-slate-400" data-testid="patient-today-actions-subtitle">{labels.todayActionsSubtitle}</p>
          </div>
        </div>
        <span className="rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-black text-slate-500" data-testid="patient-today-actions-count">
          {actions.length}
        </span>
      </div>

      {actions.length === 0 ? (
        <div className="rounded-2xl bg-emerald-50 px-4 py-3" data-testid="patient-today-actions-empty">
          <p className="text-sm font-extrabold text-emerald-800">{labels.emptyAction.title}</p>
          <p className="mt-1 text-xs font-semibold text-emerald-700/80">{labels.emptyAction.subtitle}</p>
          <button
            type="button"
            onClick={() => navigate("/patient/checkin?mode=spontaneous")}
            className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs font-black text-emerald-800 shadow-sm"
            data-testid="patient-today-empty-report-button"
          >
            <CircleDot className="h-4 w-4" />
            {labels.emptyAction.cta}
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="patient-today-actions-list">
          {actions.map((action) => (
            <TodayActionCard key={action.id} action={action} />
          ))}
        </div>
      )}
    </section>
  );
}

function TodayActionCard({ action }) {
  const navigate = useNavigate();
  const Icon = action.icon;
  const tone = todayTone(action.tone);
  const handleClick = () => {
    if (action.onClick) return action.onClick();
    if (action.to) navigate(action.to);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full rounded-2xl border px-3 py-3 text-left transition-[transform,background-color,border-color] duration-200 active:scale-[0.985] ${tone.card}`}
      data-testid={`patient-today-action-${action.id}`}
    >
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl ${tone.icon}`} data-testid={`patient-today-action-${action.id}-icon`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-black leading-tight text-slate-800" data-testid={`patient-today-action-${action.id}-title`}>{action.title}</span>
          <span className="mt-0.5 block text-xs font-semibold leading-snug text-slate-500" data-testid={`patient-today-action-${action.id}-reason`}>{action.reason}</span>
        </span>
        <span className="flex flex-col items-end gap-1">
          {action.badge ? <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-slate-700 shadow-sm" data-testid={`patient-today-action-${action.id}-badge`}>{action.badge}</span> : null}
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </span>
      </div>
    </button>
  );
}

function PatientJourneyTimeline({ items, title }) {
  return (
    <section className="animate-fade-in-up stagger-2 rounded-[1.55rem] border border-slate-100 bg-[#F8FAF9] p-3" data-testid="patient-journey-timeline-panel">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-[#2C5E53] shadow-sm" data-testid="patient-journey-timeline-icon">
          <Route className="h-4 w-4" />
        </span>
        <h2 className="text-base font-black text-slate-800" data-testid="patient-journey-timeline-title">{title}</h2>
      </div>
      <div className="space-y-0" data-testid="patient-journey-timeline-items">
        {items.map((item, idx) => {
          const state = timelineState(item.state);
          const Icon = item.icon;
          return (
            <div key={item.id} className="relative flex gap-3 pb-3 last:pb-0" data-testid={`patient-journey-timeline-item-${item.id}`}>
              {idx < items.length - 1 && <span className="absolute left-[18px] top-9 h-[calc(100%-28px)] w-px bg-slate-200" data-testid={`patient-journey-timeline-line-${item.id}`} />}
              <span className={`z-10 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl border ${state.dot}`} data-testid={`patient-journey-timeline-dot-${item.id}`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 pt-0.5">
                <span className="block text-sm font-black leading-tight text-slate-800" data-testid={`patient-journey-timeline-label-${item.id}`}>{item.label}</span>
                <span className="mt-0.5 block text-xs font-semibold leading-snug text-slate-500" data-testid={`patient-journey-timeline-detail-${item.id}`}>{item.detail}</span>
              </span>
              {item.speak && (
                <PatientSpeakerButton size="sm" fallbackText={item.speak} className="mt-0.5" testId={`patient-journey-timeline-speaker-${item.id}`} />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function todayTone(tone) {
  if (tone === "urgent") return { card: "bg-rose-50 border-rose-100", icon: "bg-white text-rose-700" };
  if (tone === "amber") return { card: "bg-amber-50 border-amber-100", icon: "bg-white text-amber-700" };
  if (tone === "blue") return { card: "bg-sky-50 border-sky-100", icon: "bg-white text-sky-700" };
  if (tone === "slate") return { card: "bg-slate-50 border-slate-100", icon: "bg-white text-slate-600" };
  return { card: "bg-emerald-50 border-emerald-100", icon: "bg-white text-emerald-700" };
}

function timelineState(state) {
  if (state === "done") return { dot: "bg-emerald-50 border-emerald-100 text-emerald-700" };
  if (state === "next") return { dot: "bg-amber-50 border-amber-100 text-amber-700" };
  return { dot: "bg-white border-[#2C5E53]/10 text-[#2C5E53]" };
}

function buildTodayActions({ L, checkinDue, hasCheckedInToday, urgentOralDoses, pendingQuestionnaireCount, unreadMessages, appointmentToConfirm, nextChemoAppointment, educationRecommendation, setShowPreChemoScanner }) {
  const actions = [];
  if (checkinDue && !hasCheckedInToday) {
    actions.push({ id: "checkin", icon: Activity, tone: "urgent", title: L.todayCheckinTitle, reason: L.todayCheckinReason, badge: "2 min", to: "/patient/checkin?mode=scheduled" });
  }
  if (urgentOralDoses.length > 0) {
    actions.push({ id: "oral-adherence", icon: Pill, tone: "amber", title: L.todayOralTitle, reason: L.todayOralReason.replace("{count}", urgentOralDoses.length), badge: urgentOralDoses.length, to: "/patient/oral-adherence" });
  }
  if (pendingQuestionnaireCount > 0) {
    actions.push({ id: "questionnaires", icon: ClipboardList, tone: "blue", title: L.todayQuestionnaireTitle, reason: L.todayQuestionnaireReason.replace("{count}", pendingQuestionnaireCount), badge: pendingQuestionnaireCount, to: "/patient/questionnaires" });
  }
  if (appointmentToConfirm) {
    actions.push({ id: "appointment-confirm", icon: Calendar, tone: "blue", title: L.todayAppointmentTitle, reason: L.todayAppointmentReason, to: "/patient/appointments" });
  }
  if (nextChemoAppointment) {
    actions.push({ id: "prechemo", icon: FlaskConical, tone: "slate", title: L.todayPreChemoTitle, reason: L.todayPreChemoReason, onClick: () => setShowPreChemoScanner(true) });
  }
  if (educationRecommendation) {
    actions.push({ id: "education", icon: PlayCircle, tone: "emerald", title: educationRecommendation.title || L.todayEducationTitle, reason: getEducationReason(L, educationRecommendation), badge: L.todayEducationBadge, to: `/patient/education/${educationRecommendation.id}` });
  }
  if (unreadMessages > 0) {
    actions.push({ id: "messages", icon: MessageSquare, tone: "slate", title: L.todayMessageTitle, reason: L.todayMessageReason.replace("{count}", unreadMessages), badge: unreadMessages, to: "/patient/messages" });
  }
  return actions;
}

function buildTimelineItems({ L, language, patient, checkinDue, hasCheckedInToday, lastScheduledCheckin, urgentOralDoses, pendingQuestionnaireCount, nextChemoAppointment, nextAppointment, educationRecommendation }) {
  const hasClinicalContext = Boolean(patient?.clinicalEvaluationComplete || patient?.cancerType || patient?.pathology);
  const hasTreatment = Boolean(patient?.displayProtocol || patient?.protocol || patient?.treatmentType || patient?.treatmentStartDate || patient?.therapeuticLine);
  const activeDetail = checkinDue
    ? L.timelineCheckinDue
    : hasCheckedInToday
      ? L.timelineCheckinDone
      : lastScheduledCheckin
        ? L.timelineHomeMonitoring
        : L.timelineHomeStart;
  const next = getNextTimelineStep({ L, urgentOralDoses, pendingQuestionnaireCount, nextChemoAppointment, educationRecommendation, checkinDue });
  return [
    { id: "diagnosis", icon: hasClinicalContext ? CheckCircle : Stethoscope, state: hasClinicalContext ? "done" : "current", label: L.timelineDiagnosis, detail: hasClinicalContext ? getDiagnosisDetail(patient, L) : L.timelineDiagnosisPending },
    { id: "treatment-plan", icon: hasTreatment ? CheckCircle : Pill, state: hasTreatment ? "done" : "current", label: L.timelineTreatment, detail: hasTreatment ? getTreatmentDetail(patient, L) : L.timelineTreatmentPending },
    { id: "home-followup", icon: Activity, state: "current", label: L.timelineFollowup, detail: activeDetail },
    ...(nextAppointment ? [{ id: "next-appointment", icon: Calendar, state: "next", label: L.timelineAppointment, detail: formatAppointmentDetail(nextAppointment, language), speak: formatAppointmentSpeech(nextAppointment, language, L) }] : []),
    { id: "next-step", icon: next.icon, state: "next", label: L.timelineNext, detail: next.detail },
  ];
}

function formatAppointmentDetail(appt, language) {
  const date = new Date(appt.date);
  const day = date.toLocaleDateString(language === "ar" ? "ar-MA" : "fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const time = date.toLocaleTimeString(language === "ar" ? "ar-MA" : "fr-FR", { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time} — ${appt.type || ""}`.trim();
}

function formatAppointmentSpeech(appt, language, L) {
  const date = new Date(appt.date);
  const locale = language === "ar" ? "ar-MA" : "fr-FR";
  const day = date.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
  const time = date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  return L.timelineAppointmentSpeech.replace("{day}", day).replace("{time}", time).replace("{type}", appt.type || "");
}

function getNextAppointment(appointments) {
  const now = new Date();
  return (appointments || [])
    .filter(appt => appt.date && new Date(appt.date) >= now && ["CONFIRMED", "PROPOSED"].includes(appt.status))
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;
}

function getNextTimelineStep({ L, urgentOralDoses, pendingQuestionnaireCount, nextChemoAppointment, educationRecommendation, checkinDue }) {
  if (checkinDue) return { icon: Activity, detail: L.timelineNextCheckin };
  if (urgentOralDoses.length > 0) return { icon: Pill, detail: L.timelineNextOral };
  if (pendingQuestionnaireCount > 0) return { icon: ClipboardList, detail: L.timelineNextQuestionnaire };
  if (nextChemoAppointment) return { icon: FlaskConical, detail: L.timelineNextPreChemo };
  if (educationRecommendation) return { icon: BookOpen, detail: L.timelineNextEducation };
  return { icon: MessageSquare, detail: L.timelineNextTeam };
}

function getDiagnosisDetail(patient, L) {
  return patient?.cancerType || patient?.pathology || L.timelineDiagnosisRecorded;
}

function getTreatmentDetail(patient, L) {
  return patient?.displayProtocol || patient?.protocol || patient?.treatmentType || L.timelineTreatmentRecorded;
}

function getEducationReason(L, content) {
  if (content?.category === "BIENVENUE") return L.todayEducationWelcome;
  if (content?.category === "TRAITEMENT") return L.todayEducationTreatment;
  if (content?.category === "EFFETS_SECONDAIRES" || content?.category === "GESTION_SYMPTOMES") return L.todayEducationSymptoms;
  return L.todayEducationReason;
}

function getNextChemoAppointment(appointments) {
  const now = new Date();
  const inSevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return (appointments || [])
    .filter(appt => {
      const date = appt.date ? new Date(appt.date) : null;
      const type = String(appt.type || "").toLowerCase();
      return date && date >= now && date <= inSevenDays && (type.includes("chimio") || type.includes("hdj"));
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;
}


function intentionTone(tone) {
  if (tone === "rose") return { card: "bg-rose-50 border-rose-100 text-rose-950", icon: "bg-white text-rose-700", status: "bg-white text-rose-800" };
  if (tone === "blue") return { card: "bg-sky-50 border-sky-100 text-sky-950", icon: "bg-white text-sky-700", status: "bg-white text-sky-800" };
  if (tone === "terracotta") return { card: "bg-orange-50 border-orange-100 text-orange-950", icon: "bg-white text-orange-700", status: "bg-white text-orange-800" };
  if (tone === "amber") return { card: "bg-amber-50 border-amber-100 text-amber-950", icon: "bg-white text-amber-700", status: "bg-white text-amber-800" };
  if (tone === "sage") return { card: "bg-emerald-50 border-emerald-100 text-emerald-950", icon: "bg-white text-emerald-700", status: "bg-white text-emerald-800" };
  if (tone === "green") return { card: "bg-teal-50 border-teal-100 text-teal-950", icon: "bg-white text-teal-700", status: "bg-white text-teal-800" };
  return { card: "bg-slate-50 border-slate-200 text-slate-900", icon: "bg-white text-slate-700", status: "bg-white text-slate-700" };
}

function IntentionCard({ to, onClick, icon: Icon, title, subtitle, badge = 0, tone, testId, audioKey, audioText, audioCache }) {
  const navigate = useNavigate();
  const toneClass = intentionTone(tone);
  const handleClick = () => {
    if (onClick) return onClick();
    if (to) navigate(to);
  };
  return (
    <div className={`relative min-h-[118px] overflow-hidden rounded-[1.35rem] border p-3 shadow-[0_8px_22px_rgb(15,23,42,0.04)] transition-[transform,box-shadow] duration-150 active:scale-[0.985] ${toneClass.card}`} data-testid={testId}>
      <button
        type="button"
        onClick={handleClick}
        className="block w-full text-left"
        data-testid={`${testId}-button`}
      >
        <div className="flex items-start gap-2.5">
          <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl shadow-sm ${toneClass.icon}`} data-testid={`${testId}-icon-box`}>
            <Icon className="h-5 w-5" strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1 pr-3 pt-1">
            <span className="block text-sm font-black leading-tight" data-testid={`${testId}-title`}>{title}</span>
            {subtitle && <span className="mt-1 block text-[11px] font-semibold leading-snug opacity-70" data-testid={`${testId}-subtitle`}>{subtitle}</span>}
          </span>
          <ChevronRight className="absolute right-3 top-4 h-4 w-4 opacity-55" data-testid={`${testId}-chevron`} />
        </div>
        {badge > 0 ? <span className="absolute left-3 bottom-3 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-black text-white ring-2 ring-white" data-testid={`${testId}-badge-dot`}>{badge > 9 ? "9+" : badge}</span> : null}
      </button>
      <div className="absolute bottom-2.5 right-2.5">
        <PatientSpeakerButton audioKey={audioKey} audioCache={audioCache} fallbackText={audioText} size="sm" testId={`${testId}-speaker`} />
      </div>
    </div>
  );
}

function getPatientHomeCopy(language) {
  if (language === "ar") {
    return {
      title: "ما أريد فعله الآن",
      quickActionsTitle: "اختصاراتي",
      todayActionsTitle: "ما يجب فعله اليوم",
      todayActionsSubtitle: "الأولويات المفيدة لمتابعتك",
      timelineTitle: "مساري",
      todayCheckinTitle: "إجراء الشيك إن",
      todayCheckinReason: "متابعة قصيرة منتظرة اليوم.",
      todayOralTitle: "تأكيد تناول الدواء",
      todayOralReason: "{count} جرعة تحتاج إلى تأكيد.",
      todayQuestionnaireTitle: "الإجابة على الاستبيان",
      todayQuestionnaireReason: "{count} استبيان مطلوب من الفريق.",
      todayAppointmentTitle: "تأكيد الموعد",
      todayAppointmentReason: "الفريق يقترح عليك موعداً.",
      todayPreChemoTitle: "إرسال نتائج التحاليل",
      todayPreChemoReason: "مفيد قبل جلسة العلاج القادمة.",
      todayEducationTitle: "فيديو موصى به",
      todayEducationReason: "محتوى مناسب لمسارك.",
      todayEducationWelcome: "للبدء وفهم الخطوات القادمة.",
      todayEducationTreatment: "لأنه مرتبط بعلاجك الحالي.",
      todayEducationSymptoms: "لأنه يساعدك على فهم الأعراض ومتابعتها.",
      todayEducationBadge: "فيديو",
      todayMessageTitle: "قراءة رسالة الفريق",
      todayMessageReason: "{count} رسالة غير مقروءة.",
      emptyAction: {
        title: "لا يوجد إجراء إلزامي الآن",
        subtitle: "إذا تغيّر شيء في حالتك، يمكنك إبلاغ الفريق.",
        cta: "الإبلاغ عن مشكلة",
      },
      timelineDiagnosis: "التشخيص مسجل",
      timelineDiagnosisRecorded: "ملفك السريري مفعّل.",
      timelineDiagnosisPending: "سيكمل الفريق هذه المرحلة.",
      timelineTreatment: "خطة العلاج",
      timelineTreatmentRecorded: "تم تسجيل علاجك.",
      timelineTreatmentPending: "سيظهر هنا عند إضافته.",
      timelineFollowup: "المتابعة في المنزل",
      timelineHomeMonitoring: "هنا نتابع الأعراض والرسائل والوثائق.",
      timelineHomeStart: "ستبدأ المتابعة بعد أول إجراء.",
      timelineCheckinDue: "الشيك إن منتظر اليوم.",
      timelineCheckinDone: "تم تحديث المتابعة اليوم.",
      timelineNext: "الخطوة القادمة",
      timelineAppointment: "الموعد القادم",
      timelineAppointmentSpeech: "موعدك القادم يوم {day} على الساعة {time}، من أجل {type}. لا تنسَ إحضار وثائقك.",
      timelineNextCheckin: "إجراء الشيك إن اليوم.",
      timelineNextOral: "تأكيد جرعات العلاج في المنزل.",
      timelineNextQuestionnaire: "الإجابة على الاستبيان المطلوب.",
      timelineNextPreChemo: "إرسال التحاليل قبل الجلسة القادمة.",
      timelineNextEducation: "مشاهدة المحتوى الموصى به.",
      timelineNextTeam: "التواصل مع الفريق إذا احتجت.",
      checkinTitle: "الشيك إن",
      checkinSubtitle: "المتابعة المخططة لحالتك.",
      reportTitle: "الإبلاغ عن مشكلة",
      reportSubtitle: "خارج التتبع اليومي أو عند ظهور عرض جديد.",
      questionnairesTitle: "استبياناتي",
      questionnairesSubtitle: "أسئلة المتابعة الطبية خطوة بخطوة.",
      treatmentTitle: "علاجي",
      treatmentSubtitle: "تأكيد الجرعات المأخوذة أو غير المأخوذة.",
      preChemoTitle: "إرسال نتائجي",
      preChemoSubtitle: "تحاليل ما قبل العلاج الكيميائي للفريق التمريضي.",
      otherDocsTitle: "وثائق أخرى",
      otherDocsSubtitle: "إضافة وثيقة مباشرة إلى ملفي.",
      folderTitle: "ملفي",
      folderSubtitle: "رؤية وثائقي الطبية.",
      educationTitle: "التثقيف",
      educationSubtitle: "نصائح ومحتوى تعليمي.",
      anytime: "عند الحاجة",
      toTeam: "للفريق",
      toFolder: "إلى الملف",
      open: "فتح",
    };
  }
  return {
    title: "Mes actions",
    quickActionsTitle: "Mes accès rapides",
    todayActionsTitle: "À faire aujourd’hui",
    todayActionsSubtitle: "Les priorités utiles pour votre suivi",
    timelineTitle: "Mon parcours",
    todayCheckinTitle: "Faire mon check-in",
    todayCheckinReason: "Suivi prévu aujourd’hui, environ 2 minutes.",
    todayOralTitle: "Confirmer ma prise",
    todayOralReason: "{count} prise(s) à confirmer aujourd’hui.",
    todayQuestionnaireTitle: "Répondre au questionnaire",
    todayQuestionnaireReason: "{count} questionnaire(s) demandé(s) par l’équipe.",
    todayAppointmentTitle: "Confirmer mon rendez-vous",
    todayAppointmentReason: "Votre équipe vous propose un créneau.",
    todayPreChemoTitle: "Envoyer mes résultats",
    todayPreChemoReason: "Utile avant votre prochaine séance.",
    todayEducationTitle: "Vidéo recommandée",
    todayEducationReason: "Contenu utile pour votre parcours.",
    todayEducationWelcome: "Pour comprendre les prochaines étapes.",
    todayEducationTreatment: "Parce qu’il est lié à votre traitement.",
    todayEducationSymptoms: "Pour mieux comprendre et suivre vos symptômes.",
    todayEducationBadge: "Vidéo",
    todayMessageTitle: "Lire le message de l’équipe",
    todayMessageReason: "{count} message(s) non lu(s).",
    emptyAction: {
      title: "Rien d’obligatoire pour le moment",
      subtitle: "Si quelque chose change, vous pouvez prévenir l’équipe.",
      cta: "Signaler un problème",
    },
    timelineDiagnosis: "Diagnostic enregistré",
    timelineDiagnosisRecorded: "Votre dossier clinique est actif.",
    timelineDiagnosisPending: "Votre équipe complètera cette étape.",
    timelineTreatment: "Plan de traitement",
    timelineTreatmentRecorded: "Votre traitement est renseigné.",
    timelineTreatmentPending: "Il apparaîtra ici quand il sera ajouté.",
    timelineFollowup: "Suivi à domicile",
    timelineHomeMonitoring: "Symptômes, messages et documents sont suivis ici.",
    timelineHomeStart: "Le suivi commence avec vos premières actions.",
    timelineCheckinDue: "Votre check-in est attendu aujourd’hui.",
    timelineCheckinDone: "Votre suivi a été mis à jour aujourd’hui.",
    timelineNext: "Prochaine étape",
    timelineAppointment: "Prochain rendez-vous",
    timelineAppointmentSpeech: "Votre prochain rendez-vous est {day} à {time}, pour {type}. Pensez à apporter vos documents.",
    timelineNextCheckin: "Faire votre check-in aujourd’hui.",
    timelineNextOral: "Confirmer vos prises à domicile.",
    timelineNextQuestionnaire: "Répondre au questionnaire demandé.",
    timelineNextPreChemo: "Envoyer vos résultats avant la prochaine séance.",
    timelineNextEducation: "Regarder le contenu recommandé.",
    timelineNextTeam: "Contacter l’équipe si besoin.",
    checkinTitle: "Check‑in",
    checkinSubtitle: "Mon suivi prévu.",
    reportTitle: "Signaler un problème",
    reportSubtitle: "Hors check-in, si quelque chose change.",
    questionnairesTitle: "Mes questionnaires",
    questionnairesSubtitle: "Répondre aux PRO et suivis médicaux.",
    treatmentTitle: "Mon traitement",
    treatmentSubtitle: "Confirmer mes prises à domicile.",
    preChemoTitle: "Envoyer mes résultats",
    preChemoSubtitle: "Bilans pré-chimio transmis à l’IDE.",
    otherDocsTitle: "Autres documents",
    otherDocsSubtitle: "Ajouter directement à mon dossier.",
    folderTitle: "Mon dossier",
    folderSubtitle: "Voir mes documents médicaux.",
    educationTitle: "Éducation",
    educationSubtitle: "Conseils et contenus utiles.",
    anytime: "Si besoin",
    toTeam: "Équipe IDE",
    toFolder: "Dossier",
    open: "Ouvrir",
  };
}

function serviceToneClass(tone) {
  if (tone === "blue") return "bg-sky-50 text-sky-700 border-sky-100";
  if (tone === "green") return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (tone === "amber") return "bg-amber-50 text-amber-700 border-amber-100";
  return "bg-slate-50 text-slate-700 border-slate-100";
}

function ServiceTile({ to, icon: Icon, title, badge = 0, tone, testId, audioKey, audioText, audioCache }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid={testId}
      className="relative min-h-[96px] rounded-2xl border border-[#2C5E53]/10 bg-white/92 p-3 shadow-[0_8px_22px_rgb(15,23,42,0.04)] transition-[transform,background-color] duration-200 hover:bg-[#F8FAF9] active:scale-[0.98]"
    >
      <button
        type="button"
        onClick={() => navigate(to)}
        className="block w-full text-left"
        data-testid={`${testId}-button`}
      >
        <span className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl border ${serviceToneClass(tone)}`} data-testid={`${testId}-icon-box`}>
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </span>
        <span className="block pr-8 text-sm font-extrabold leading-tight text-slate-800" data-testid={`${testId}-title`}>{title}</span>
      </button>
      <span className="absolute right-2 bottom-2">
        <PatientSpeakerButton audioKey={audioKey} audioCache={audioCache} fallbackText={audioText} size="sm" testId={`${testId}-speaker`} />
      </span>
      {badge > 0 && (
        <span className="absolute right-2 top-2 min-w-5 rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[10px] font-extrabold text-white" data-testid={`${testId}-badge`}>
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </div>
  );
}

function CheckinFrequencyInfo({ patient, hasCheckedInToday, checkinDue }) {
  const { t } = useLanguage();
  const frequency = patient.checkinFrequency || "DAILY";
  const FREQ_INFO = {
    DAILY: { icon: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    EVERY_2_DAYS: { icon: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    "3X_WEEK": { icon: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
    "1X_WEEK": { icon: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
  };
  const info = FREQ_INFO[frequency] || FREQ_INFO.DAILY;
  const freqLabel = t(`patient.frequency.${frequency}`) || frequency;

  const getNextCheckinText = () => {
    if (hasCheckedInToday) return t("patient.frequency.upToDate");
    if (checkinDue) return t("patient.frequency.nextToday");
    return t("patient.frequency.notToday");
  };

  return (
    <div className={`rounded-2xl border px-3 py-2.5 ${info.bg} ${info.border} animate-fade-in-up stagger-3`} data-testid="checkin-frequency-info">
      <div className="flex items-center gap-2.5">
        <Clock className={`w-4 h-4 ${info.icon}`} data-testid="checkin-frequency-icon" />
        <div>
          <p className={`text-xs font-extrabold ${info.icon}`} data-testid="checkin-frequency-title">{t("patient.frequency.title")} {freqLabel}</p>
          <p className="text-[11px] text-slate-500 mt-0.5" data-testid="checkin-frequency-next">
            {hasCheckedInToday ? t("patient.frequency.upToDate") : `${t("patient.frequency.nextCheckin")} : ${getNextCheckinText()}`}
          </p>
        </div>
      </div>
    </div>
  );
}

